import { DialogContent, Drawer } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { usePageScrollLock } from "../hooks/usePageScrollLock";

type SidePanelControls = {
  close: () => void;
};

type SidePanelProps = {
  ariaLabelledBy: string;
  children: ReactNode | ((controls: SidePanelControls) => ReactNode);
  className?: string;
  contentClassName?: string;
  dataTestId?: string;
  onClose: () => void;
  onEntered?: () => void;
  open?: boolean;
};

export function SidePanel({
  ariaLabelledBy,
  children,
  className,
  contentClassName,
  dataTestId,
  onClose,
  onEntered,
  open,
}: SidePanelProps) {
  const isControlled = open !== undefined;
  const [autoOpen, setAutoOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const panelOpen = isControlled ? open : autoOpen;
  const paperClassName = ["record-side-panel", className].filter(Boolean).join(" ");
  const panelContentClassName = ["records-modal-content", "record-side-panel-content", contentClassName].filter(Boolean).join(" ");
  usePageScrollLock(panelOpen);

  const close = useCallback(() => {
    if (isControlled) {
      onClose();
      return;
    }
    setAutoOpen(false);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      onClose();
    }, 260);
  }, [isControlled, onClose]);

  useEffect(() => {
    if (isControlled) return;
    let frame = 0;
    frame = window.requestAnimationFrame(() => setAutoOpen(true));
    return () => {
      window.cancelAnimationFrame(frame);
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, [isControlled]);

  return (
    <Drawer
      anchor="right"
      open={panelOpen}
      onClose={close}
      aria-labelledby={ariaLabelledBy}
      ModalProps={{ disableScrollLock: true }}
      slotProps={{
        paper: { className: paperClassName },
        transition: onEntered ? { onEntered } : undefined,
      }}
    >
      <DialogContent className={panelContentClassName} data-testid={dataTestId}>
        {typeof children === "function" ? children({ close }) : children}
      </DialogContent>
    </Drawer>
  );
}
